import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { INamedFontMarkerFeatureStyleParams } from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";
import {
  AddOutlined,
  ColorLensOutlined,
  Delete,
  TableRowsOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import layerShapes, { Shapes } from "./layerShapes";
import LayerViewPrototype from "./LayerViewPrototype";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { IUnsavedLayerChanges } from "../../../../../../../common/utils/layers/layerutils";

export default function LayerSettings({
  unsavedLayerChanges,
  setUnsavedLayerChanges,
  editStyle = true,
  editProperties = false,
  editPropertyDefDataType = true,
  canSaveCBF,
}: {
  unsavedLayerChanges: IUnsavedLayerChanges;
  setUnsavedLayerChanges: Dispatch<SetStateAction<IUnsavedLayerChanges>>;
  editStyle?: boolean;
  editProperties?: boolean;
  editPropertyDefDataType?: boolean;
  canSaveCBF?: (canSave: boolean) => void;
}) {
  const { t } = useTranslation();
  const darkTheme = defaultStore((state) => state.darkTheme);
  const activeAddLayersListItem = sidebarStore(
    (state) => state.activeAddLayersListItem
  );
  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );

  const shapes: Shapes[] = layerShapes();

  const [activeTab, setActiveTab] = useState("attributes");
  const handleChangeTab = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  //check if can save layer changes
  const checkIfCanSave = () => {
    if (!canSaveCBF) return;
    const propertyDefs = unsavedLayerChanges.propertydef.applied;
    if (propertyDefs.length === 1) {
      canSaveCBF(true);
    } else {
      let canSave = true;
      let keys: string[] = [];
      for (let i = 0; i < propertyDefs.length; i++) {
        const item = propertyDefs[i];
        if (item.key === "" || keys.includes(item.key)) {
          canSave = false;
          break;
        }
        keys.push(item.key);
      }
      canSaveCBF(canSave);
    }
  };

  //check if can save layer changes
  useEffect(() => {
    checkIfCanSave();
  }, [unsavedLayerChanges.propertydef.applied.length]);

  // ── Property row handlers ────────────────────────────────────────────────

  const handlePropertyRowName = (value: string, item: mm.LayerPropertyDef) => {
    // Save the old key before updating the applied property definition
    const oldKey = item.key;

    item.key = value;
    item.name = value;

    if (!unsavedLayerChanges.propertydef.add.includes(item)) {
      const existingRenameIndex =
        unsavedLayerChanges.propertydef.rename.findIndex((rename) => {
          return rename.newkey === oldKey;
        });

      if (existingRenameIndex !== -1) {
        unsavedLayerChanges.propertydef.rename[existingRenameIndex].newkey =
          value;
      } else {
        unsavedLayerChanges.propertydef.rename.push({
          oldkey: oldKey,
          newkey: value,
        });
      }
    }

    checkIfCanSave();
  };

  const handlePropertyRowDataType = (
    value: mm.LAYERPROPERTYTYPE,
    index: number
  ) => {
    unsavedLayerChanges.propertydef.applied[index].datatype = value;

    checkIfCanSave();
  };

  const addPropertyRow = () => {
    const newProperty = new mm.LayerPropertyDef(
      "",
      mm.LAYERPROPERTYTYPE.STRING
    );
    unsavedLayerChanges.propertydef.add.push(newProperty);
    unsavedLayerChanges.propertydef.applied.push(newProperty);

    setUnsavedLayerChanges({ ...unsavedLayerChanges });
  };

  const deletePropertyRow = (position: number) => {
    const deletedProperty = unsavedLayerChanges.propertydef.applied[position];

    const indexInAdd = unsavedLayerChanges.propertydef.add.findIndex(
      (item) => item.key === deletedProperty.key
    );
    if (indexInAdd !== -1) {
      unsavedLayerChanges.propertydef.add.splice(indexInAdd, 1);
    } else {
      unsavedLayerChanges.propertydef.remove.push(deletedProperty.name);
    }

    unsavedLayerChanges.propertydef.applied.splice(position, 1);

    setUnsavedLayerChanges({ ...unsavedLayerChanges });
  };

  const blockSaveButtonOnFocus = () => {
    changeSaveButtonStateOnAddNewLayers(false);
  };

  // ── Color helpers ────────────────────────────────────────────────────────

  const hexToRgb = (hex: string, transparency: number): number[] => {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }
    const bigint = parseInt(hex, 16);
    const r = Math.round((((bigint >> 16) & 255) / 255) * 100) / 100;
    const g = Math.round((((bigint >> 8) & 255) / 255) * 100) / 100;
    const b = Math.round(((bigint & 255) / 255) * 100) / 100;
    return [r, g, b, transparency];
  };

  const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // ── Style handlers — all operate on a clone of unsavedStyleChanges ────────────

  const handleColor = debounce((index: number, value: string) => {
    const rule = unsavedLayerChanges.style.rules[index];
    if (rule.symbolizer === "polygon") {
      rule.params.fill = hexToRgb(value, rule.params.fill[3]);
    } else {
      rule.params.stroke = hexToRgb(
        value,
        rule.params.stroke ? rule.params.stroke[3] : 1
      );
    }
    setUnsavedLayerChanges({ ...unsavedLayerChanges }); // Trigger re-render
  }, 100);

  const handleShape = (index: number, value: string | number | boolean) => {
    const rule = unsavedLayerChanges.style.rules[index];
    const shapeValue = value as
      | "square-o"
      | "square"
      | "location-arrow"
      | "star-o"
      | "star"
      | "map-pin"
      | "map-marker"
      | "crosshairs"
      | "circle-o"
      | "circle"
      | "cross";

    if (rule.symbolizer === "point") {
      if ("font-marker" in rule.params) {
        (rule.params as INamedFontMarkerFeatureStyleParams)[
          "font-marker"
        ].name = shapeValue;
      } else {
        rule.params = {
          "font-marker": {
            name: shapeValue,
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        };
      }
    }
    setUnsavedLayerChanges({ ...unsavedLayerChanges }); // Trigger re-render
  };

  const handleSize = (index: number, value: number) => {
    const rule = unsavedLayerChanges.style.rules[index];
    if (rule.symbolizer === "point") {
      rule.params.size = value;
      if ("font-marker" in rule.params) {
        (rule.params as INamedFontMarkerFeatureStyleParams)[
          "font-marker"
        ].fontsize = value;
      }
    } else if (rule.symbolizer === "line") {
      rule.params["stroke-width"] = value;
    }
    setUnsavedLayerChanges({ ...unsavedLayerChanges }); // Trigger re-render
  };

  const handleTransparency = (index: number, value: number) => {
    const rule = unsavedLayerChanges.style.rules[index];
    if (rule.symbolizer === "polygon") {
      rule.params.fill[3] = Math.round(100 - value) / 100;
    } else {
      if (rule.params.stroke)
        rule.params.stroke[3] = Math.round(100 - value) / 100;
    }
    setUnsavedLayerChanges({ ...unsavedLayerChanges }); // Trigger re-render
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const LayerPropertyRow = ({
    item,
    index,
  }: {
    item: mm.LayerPropertyDef;
    index: number;
  }) => {
    const dataTypeEditable =
      editProperties &&
      index !== 0 &&
      (editPropertyDefDataType ||
        unsavedLayerChanges.propertydef.add.includes(item));

    return (
      <Box
        key={`${index}_${item.name}_box`}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: { xs: 1, md: 2 },
        }}
      >
        <TextField
          color="primary"
          sx={{ width: { xs: "calc(50% - 4px)", md: "25%" } }}
          size="small"
          label={t("create_layer.property_name")}
          variant="outlined"
          defaultValue={item.name}
          disabled={!editProperties || index === 0}
          onChange={(event) => handlePropertyRowName(event.target.value, item)}
          onBlur={checkIfCanSave}
          onFocus={blockSaveButtonOnFocus}
        />
        <FormControl
          color="primary"
          size="small"
          sx={{ width: { xs: "calc(50% - 52px)", md: "25%" } }}
        >
          <InputLabel>{t("create_layer.data_type")}</InputLabel>
          <Select
            disabled={!dataTypeEditable}
            label={t("create_layer.data_type")}
            defaultValue={item.datatype}
            onChange={(event) =>
              handlePropertyRowDataType(
                event.target.value as mm.LAYERPROPERTYTYPE,
                index - 1
              )
            }
          >
            <MenuItem value={mm.LAYERPROPERTYTYPE.STRING}>
              {t("create_layer.data_type_2")}
            </MenuItem>
            <MenuItem value={mm.LAYERPROPERTYTYPE.NUMBER}>
              {t("create_layer.data_type_3")}
            </MenuItem>
            {activeAddLayersListItem !== "create_new_layer" && (
              <MenuItem value={mm.LAYERPROPERTYTYPE.ENUM}>
                {t("create_layer.data_type_4")}
              </MenuItem>
            )}
            <MenuItem value={mm.LAYERPROPERTYTYPE.BOOLEAN}>
              {t("create_layer.data_type_5")}
            </MenuItem>
            <MenuItem value={mm.LAYERPROPERTYTYPE.DATE}>
              {t("create_layer.data_type_6")}
            </MenuItem>
            <MenuItem value={mm.LAYERPROPERTYTYPE.INTEGER}>
              {t("create_layer.data_type_7")}
            </MenuItem>
          </Select>
        </FormControl>
        {!(!editProperties || index === 0) && (
          <IconButton
            onClick={() =>
              monitor(apm, "delete-property | Add", () =>
                deletePropertyRow(index - 1)
              )
            }
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
          >
            <Delete />
          </IconButton>
        )}
      </Box>
    );
  };

  const LayerPropertyRows = useMemo(() => {
    return (
      <>
        <LayerPropertyRow
          key={"0_ID"}
          item={new mm.LayerPropertyDef("ID", mm.LAYERPROPERTYTYPE.STRING)}
          index={0}
        />
        {unsavedLayerChanges.propertydef.applied.map(
          (item: mm.LayerPropertyDef, index: number) => {
            if (item.key === "ID" || item.key === "id") return null; // Skip the ID property since it's already rendered
            return (
              <LayerPropertyRow
                key={`${index + 1}_${item.name}`}
                item={item}
                index={index + 1}
              />
            );
          }
        )}
      </>
    );
  }, [unsavedLayerChanges.propertydef.applied.length, editPropertyDefDataType]);

  const defaultColor = darkTheme ? "white" : "darkblue.main";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 600,
          color: darkTheme ? "white" : "darkblue.main",
        }}
      >
        {t("create_layer.settings")}
      </Typography>
      <TabContext value={activeTab}>
        <TabList
          onChange={handleChangeTab}
          aria-label="lab API tabs example"
          textColor="secondary"
          indicatorColor="secondary"
          sx={{
            minHeight: 32,
            "& .MuiTab-root": { minHeight: 32, color: defaultColor },
          }}
        >
          <Tab
            label={t("create_layer.property_def")}
            value="attributes"
            iconPosition="start"
            icon={<TableRowsOutlined fontSize="small" />}
          />
          <Tab
            label={
              <Tooltip
                title={editStyle ? "" : t("tooltip.style_readonly")}
                placement="top"
                classes={{ tooltip: "infra3d-buttonDescription" }}
              >
                <span>{t("create_layer.style_def")}</span>
              </Tooltip>
            }
            value="styles"
            iconPosition="start"
            icon={<ColorLensOutlined fontSize="small" />}
            disabled={!editStyle}
            sx={{
              "&.Mui-disabled": {
                pointerEvents: "auto",
                cursor: "not-allowed",
              },
            }}
          />
        </TabList>

        {/* ── Attributes tab ── */}
        <TabPanel value="attributes" sx={{ padding: 0 }}>
          <Box
            sx={{
              marginTop: 1,
              display: "flex",
              gap: 2,
              flexDirection: "column",
            }}
          >
            {LayerPropertyRows}
            {editProperties && (
              <Button
                variant="outlined"
                sx={{
                  width: "fit-content",
                  borderColor: darkTheme ? "white" : "darkblue.main",
                  color: darkTheme ? "white" : "darkblue.main",
                }}
                onClick={() =>
                  monitor(apm, "add-property | Add", addPropertyRow)
                }
              >
                <AddOutlined fontSize="small" />
                {t("manage_layers.button_add")}
              </Button>
            )}
          </Box>
        </TabPanel>

        {/* ── Styles tab ── */}
        <TabPanel value="styles" sx={{ padding: 0 }}>
          <Box
            sx={{
              marginTop: 1,
              paddingX: 1,
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Preview box */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                minWidth: "200px",
                gap: 2,
                padding: 2,
                borderRadius: "8px",
                boxShadow: 2,
                color: darkTheme ? "white" : "darkblue.main",
              }}
            >
              <LayerViewPrototype
                shapes={shapes}
                styles={unsavedLayerChanges.style.rules}
              />
            </Box>

            {/* One card per style rule — render all three symbolizers (point, line, polygon) */}
            {unsavedLayerChanges.style.rules &&
              ["point", "line", "polygon"]
                .filter((symbolizer) => {
                  if (
                    unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.POINT
                  ) {
                    // Render only point symbolizers for point layers
                    return symbolizer === "point";
                  } else if (
                    unsavedLayerChanges.geometrytype ===
                    mm.GEOMETRYTYPE.LINESTRING
                  ) {
                    // Render point and line symbolizers for line layers, but not polygon
                    return symbolizer !== "polygon";
                  } else if (
                    unsavedLayerChanges.geometrytype === mm.GEOMETRYTYPE.POLYGON
                  ) {
                    // Render all three symbolizers for polygon layers;
                    return true;
                  }
                })
                .map((symbolizer) => {
                  let item = unsavedLayerChanges.style.rules?.find(
                    (r) => r.symbolizer === symbolizer
                  );

                  if (!item) {
                    // If the rule doesn't exist, create a default one based on the symbolizer type
                    if (symbolizer === "point") {
                      item = {
                        symbolizer: "point",
                        filter: "",
                        params: {
                          "font-marker": {
                            name: "circle",
                            rotation: 0,
                            fontsize: 20,
                            anchor: ["center", "middle"],
                          },
                          size: 20,
                          stroke: [0, 0, 0, 0],
                        },
                      };
                      unsavedLayerChanges.style.rules?.push(item as any);
                    } else if (symbolizer === "line") {
                      item = {
                        symbolizer: "line",
                        filter: "",
                        params: {
                          stroke: [0, 0, 0, 0],
                          "stroke-width": 2,
                        },
                      };
                      unsavedLayerChanges.style.rules?.push(item as any);
                    } else if (symbolizer === "polygon") {
                      item = {
                        symbolizer: "polygon",
                        filter: "",
                        params: {
                          fill: [0, 0, 0, 0],
                        },
                      };
                      unsavedLayerChanges.style.rules?.push(item as any);
                    }
                  }

                  if (!item) return null; // If item is still undefined, skip rendering

                  const index =
                    unsavedLayerChanges.style.rules?.findIndex(
                      (r) => r.symbolizer === symbolizer
                    ) ?? -1;

                  if (index === -1) return null; // If index is not found, skip rendering

                  return (
                    <Box
                      key={symbolizer}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        padding: 2,
                        borderRadius: "8px",
                        boxShadow: 2,
                        aspectRatio: "1 / 1",
                        width: {
                          xs: "calc(40% - 10px)",
                          md: "calc(20% - 16px)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          color: darkTheme ? "white" : "darkblue.main",
                        }}
                      >
                        {symbolizer === "point" &&
                          t("create_layer.style_type_1")}
                        {symbolizer === "line" &&
                          t("create_layer.style_type_2")}
                        {symbolizer === "polygon" &&
                          t("create_layer.style_type_3")}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <TextField
                          sx={{ width: "50%" }}
                          type="color"
                          size="small"
                          label={t("create_layer.style_color")}
                          variant="outlined"
                          value={
                            item.symbolizer === "polygon"
                              ? rgbToHex(
                                  item.params.fill[0],
                                  item.params.fill[1],
                                  item.params.fill[2]
                                )
                              : rgbToHex(
                                  item.params.stroke?.[0] || 0,
                                  item.params.stroke?.[1] || 0,
                                  item.params.stroke?.[2] || 0
                                )
                          }
                          onChange={(event) =>
                            handleColor(index, event.target.value)
                          }
                        />
                        {item.symbolizer === "point" && (
                          <FormControl
                            color="primary"
                            size="small"
                            sx={{ width: "50%" }}
                          >
                            <InputLabel>
                              {t("create_layer.style_shape")}
                            </InputLabel>
                            <Select
                              label={t("create_layer.style_shape")}
                              value={
                                "font-marker" in item.params &&
                                item.params["font-marker"] &&
                                "name" in item.params["font-marker"]
                                  ? item.params["font-marker"].name
                                  : "circle"
                              }
                              onChange={(event) =>
                                handleShape(index, event.target.value)
                              }
                              sx={{
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  alignItems: "center",
                                },
                              }}
                            >
                              {shapes.map((shape) => (
                                <MenuItem key={shape.value} value={shape.value}>
                                  {shape.icon}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>

                      {/* Transparency slider */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: darkTheme ? "white" : "darkblue.main",
                          }}
                        >
                          {t("create_layer.style_transparency")}
                        </Typography>
                        <Slider
                          sx={{ color: "darkblue.main" }}
                          aria-label="Small steps"
                          defaultValue={
                            item.symbolizer === "polygon"
                              ? 100 - item.params.fill[3] * 100
                              : 100 - (item.params.stroke?.[3] || 0) * 100
                          }
                          value={
                            item.symbolizer === "polygon"
                              ? 100 - item.params.fill[3] * 100
                              : 100 - (item.params.stroke?.[3] || 0) * 100
                          }
                          onChange={(_event, value: number | number[]) =>
                            handleTransparency(index, Number(value))
                          }
                        />
                      </Box>

                      {/* Size slider — points only */}
                      {item.symbolizer === "point" && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: darkTheme ? "white" : "darkblue.main",
                            }}
                          >
                            {t("create_layer.style_size")}
                          </Typography>
                          <Slider
                            sx={{ color: "darkblue.main" }}
                            aria-label="Small steps"
                            defaultValue={item.params.size}
                            min={8}
                            max={40}
                            value={item.params.size}
                            onChange={(_event, value: number | number[]) =>
                              handleSize(index, Number(value))
                            }
                          />
                        </Box>
                      )}

                      {/* Width slider — lines only */}
                      {item.symbolizer === "line" && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: darkTheme ? "white" : "darkblue.main",
                            }}
                          >
                            {t("create_layer.style_width")}
                          </Typography>
                          <Slider
                            sx={{ color: "darkblue.main" }}
                            min={1}
                            max={20}
                            aria-label="Small steps"
                            defaultValue={item.params["stroke-width"]}
                            value={item.params["stroke-width"]}
                            onChange={(_event, value: number | number[]) =>
                              handleSize(index, Number(value))
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
          </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
}
